// eslint-disable no-explicit-any

import { InterfaceObjectNotFoundError } from '@flipster/webview-bridge/transport/discoverMessageTransportType';

import type { AbstractSocketEvents, SocketState } from './AbstractSocket';
import AbstractSocket from './AbstractSocket';
import BridgeSocket from './BridgeSocket';
import Socket from './Socket';

export type HybridSocketPhase = 'bridge' | 'websocket';

export interface HybridSocketEvents extends AbstractSocketEvents {
  phaseChanged: HybridSocketPhase;
}

export default class HybridSocket<
  D extends unknown,
  E extends HybridSocketEvents = HybridSocketEvents,
> extends AbstractSocket<D, E> {
  #on = false;
  #phase: HybridSocketPhase = 'bridge';
  #messageQueue: D[] = [];

  constructor(
    public readonly bridgeSocket: BridgeSocket<any, any, any, any, any>,
    public readonly webSocket: Socket<any>,
  ) {
    super();
    const proxy = (socket: AbstractSocket<any, any>) => {
      const offs = [
        socket.on('error', (state) => this.emit('error', state)),
        socket.on('message', (state) => this.emit('message', state)),
        socket.on('stateChanged', (state) => this.emit('stateChanged', state)),
        socket.on('reconnectionLongTerm', (state) => this.emit('reconnectionLongTerm', state)),
      ];
      return (): void => void offs.map((off) => off());
    };
    const offBridgeProxy = proxy(this.bridgeSocket);
    const phaseChanged = this.bridgeSocket.on('connectionError', (error) => {
      if (!this.#on) return;
      if (!(error instanceof InterfaceObjectNotFoundError)) return;
      offBridgeProxy();
      this.#phase = 'websocket';
      this.bridgeSocket.close();
      this.webSocket.connect();
      proxy(this.webSocket);
      this.emit('phaseChanged', this.#phase);
      phaseChanged();
    });
  }

  get phase(): HybridSocketPhase {
    return this.#phase;
  }

  get debugLabel(): string {
    if (this.#phase === 'bridge') return `bridge - ${this.bridgeSocket.debugLabel}`;
    return `websocket - ${this.webSocket.debugLabel}`;
  }
  get state(): SocketState {
    if (!this.#on) return 'closed';
    if (this.#phase === 'bridge') return this.bridgeSocket.state;
    return this.webSocket.state;
  }

  connect(): void {
    this.#on = true;
    if (this.#phase === 'bridge') this.bridgeSocket.connect();
    else this.webSocket.connect();
  }
  close(): void {
    this.#on = false;
    if (this.#phase === 'bridge') this.bridgeSocket.close();
    else this.webSocket.close();
  }

  send(data: D): void {
    if (this.state !== 'connected') {
      this.#messageQueue.push(data);
    } else {
      if (this.#phase === 'bridge') this.bridgeSocket.send(data);
      else this.webSocket.send(data);
    }
  }

  protected flushQueuedMessages(): void {
    this.#messageQueue.forEach((data) => this.send(data));
  }
  protected clearQueuedMessages(): void {
    this.#messageQueue = [];
  }
}
